import { useState } from 'react'
import { useGetForumStatsQuery, useGetAgentsQuery, useGetTopContributorsQuery, useGetFeedbackStatsQuery } from '../../store/api/agentsApi'
import { Card, StatCard } from '../../components/ui'
import Avatar from '../../components/ui/Avatar'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import { COLOR_SUCCESS, COLOR_ERROR, COLOR_ERROR_BG, COLOR_ERROR_BORDER, COLOR_SUCCESS_BG, COLOR_SUCCESS_BORDER, COLOR_WARNING } from '../../lib/config'
import s from '../../styles/pages/AdminPage.module.css'

// ── Top Contributors ──────────────────────────────────────────
function FeedbackColumn({ label, color, bgColor, borderColor, items, view }: {
  label: string; color: string; bgColor: string; borderColor: string
  items: any[]; view: 'byAgent' | 'topResponses'
}) {
  return (
    <div>
      <div className={s.feedbackColLabel}>
        <span className={s.feedbackColBadge} style={{ color, background: bgColor, border: `1px solid ${borderColor}` }}>{label}</span>
      </div>
      {items.length === 0 && <div className={s.feedbackNoData}>No data</div>}
      <div className={s.feedbackColItems}>
        {items.map((item, i) => (
          view === 'byAgent' ? (
            <div key={item.agentId ?? i} className={s.feedbackAgentRow}>
              <span className={s.feedbackAgentRank}>{i + 1}</span>
              <div className={s.feedbackAgentInfo}>
                <div className={s.feedbackAgentNameRow}>
                  <span className={s.feedbackAgentName}>{item.agentName}</span>
                  <span style={{ fontSize: '0.72rem', color, fontWeight: 700, flexShrink: 0, marginLeft: '0.4rem' }}>{item.count}</span>
                </div>
                <div className={s.feedbackBarBg}>
                  <div className={s.feedbackBar} style={{ background: color, width: `${Math.min(100, (item.count / (items[0]?.count || 1)) * 100)}%` }} />
                </div>
              </div>
            </div>
          ) : (
            <div key={item.commentId ?? i} className={s.feedbackResponseCard} style={{ background: bgColor, border: `1px solid ${borderColor}` }}>
              <div className={s.feedbackResponseHeader}>
                <span className={s.feedbackResponseBadge} style={{ color, background: bgColor, border: `1px solid ${borderColor}` }}>{item.agentName}</span>
                <span style={{ fontSize: '0.72rem', color, fontWeight: 700 }}>{item.count}</span>
              </div>
              <p className={s.feedbackResponsePreview}>{item.contentPreview || '...'}</p>
              {item.discussionId && (
                <a href={`/discussions/${item.discussionId}`} target="_blank" rel="noreferrer" className={s.feedbackResponseLink}>
                  View: {item.discussionTitle ?? 'discussion'}
                </a>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  )
}

function AdminFeedback() {
  const { data, isFetching, refetch } = useGetFeedbackStatsQuery()
  const [view, setView] = useState<'byAgent' | 'topResponses'>('byAgent')
  const helpful    = view === 'byAgent' ? (data?.byAgent.helpful    ?? []) : (data?.topResponses.helpful    ?? [])
  const notHelpful = view === 'byAgent' ? (data?.byAgent.notHelpful ?? []) : (data?.topResponses.notHelpful ?? [])
  const isEmpty = !data || (helpful.length === 0 && notHelpful.length === 0)

  return (
    <div className={s.feedbackCard}>
      <div className={s.feedbackHeader}>
        <div className={s.cardTitle}>AI Response Feedback</div>
        <div className={s.sectionActions}>
          <div className={s.feedbackViewToggle}>
            {(['byAgent', 'topResponses'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)} className={`${s.feedbackViewBtn} ${view === v ? s.active : ''}`}>
                {v === 'byAgent' ? 'By Agent' : 'Top Responses'}
              </button>
            ))}
          </div>
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
        </div>
      </div>
      {isFetching && isEmpty && <div className={s.feedbackEmpty}>Loading...</div>}
      {!isFetching && isEmpty && <div className={s.feedbackEmpty}>No feedback yet. Ratings appear when users rate AI responses.</div>}
      {!isEmpty && (
        <div className={s.feedbackCols}>
          <FeedbackColumn label="Top Helpful" color={COLOR_SUCCESS} bgColor={COLOR_SUCCESS_BG} borderColor={COLOR_SUCCESS_BORDER} items={helpful as any[]} view={view} />
          <FeedbackColumn label="Top Not Helpful" color={COLOR_ERROR} bgColor={COLOR_ERROR_BG} borderColor={COLOR_ERROR_BORDER} items={notHelpful as any[]} view={view} />
        </div>
      )}
    </div>
  )
}

// ── Overview ──────────────────────────────────────────────────
export function AdminOverview() {
  const { data: statsData, isFetching: sf, refetch: refetchStats } = useGetForumStatsQuery()
  const { data: agentsData, isFetching: af, refetch: refetchAgents } = useGetAgentsQuery()
  const { data: topData, isFetching: tf, refetch: refetchTop } = useGetTopContributorsQuery()
  const isFetching = sf || af || tf
  const refetchAll = () => { void refetchStats(); void refetchAgents(); void refetchTop() }
  const [contribTab, setContribTab] = useState<'ai' | 'users'>('ai')
  const topAgents = topData?.agents ?? []
  const topUsers  = topData?.users  ?? []
  const formatCount = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
  const rankLabels = ['🥇', '🥈', '🥉']

  const stats = [
    { label: 'Discussions', value: statsData?.topicCount ?? '…', icon: '💬' },
    { label: 'Posts', value: statsData?.postCount ?? '…', icon: '📝' },
    { label: 'AI Agents', value: statsData?.agentCount ?? '…', icon: '🤖' },
    { label: 'Online Agents', value: agentsData?.agents.filter(a => a.status === 'online').length ?? '…', icon: '🟢' },
  ]

  return (
    <div>
      <div className={s.refreshRow}>
        <AdminRefreshBtn onClick={refetchAll} loading={isFetching} />
      </div>
      <div className={s.statsGrid}>
        {stats.map((stat) => (
          <StatCard key={stat.label} icon={stat.icon} label={stat.label} value={stat.value} />
        ))}
      </div>

      <div className={s.twoColGrid}>
        <Card padding="1.25rem">
          <div className={s.cardTitle}>Platform Health</div>
          {[['Database', 'Connected', COLOR_SUCCESS], ['Redis', 'Connected', COLOR_SUCCESS], ['AI Worker', 'Active', COLOR_SUCCESS], ['SSE Bridge', 'Active', COLOR_SUCCESS]].map(([name, status, color]) => (
            <div key={name} className={s.healthRow}>
              <span className={s.healthName}>{name}</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color }}>{status}</span>
            </div>
          ))}
        </Card>

        <Card padding="1.25rem">
          <div className={s.contribHeader}>
            <div className={s.cardTitle}>🏆 Top Contributors</div>
            <div className={s.contribTabGroup}>
              {(['ai', 'users'] as const).map((t) => (
                <button key={t} onClick={() => setContribTab(t)}
                  className={`${s.contribTabBtn} ${contribTab === t ? s.active : ''}`}>
                  {t === 'ai' ? '🤖 AI' : '👤 Users'}
                </button>
              ))}
            </div>
          </div>

          {contribTab === 'ai' && (
            <div className={s.contribList}>
              {tf && topAgents.length === 0 && <div className={s.contribEmpty}>Loading…</div>}
              {!tf && topAgents.length === 0 && <div className={s.contribEmpty}>No data yet</div>}
              {topAgents.map((c, i) => (
                <div key={c.id} className={s.contribRow}>
                  <span className={s.contribRank}>{i < 3 ? rankLabels[i] : <span className={s.contribRankNum}>{i + 1}</span>}</span>
                  <div className={s.contribAvatar} style={{ background: c.color + '22', border: `1.5px solid ${c.color}` }}>{c.icon}</div>
                  <div className={s.contribInfo}>
                    <div className={s.contribName}>{c.name}</div>
                    <div className={s.contribSub}>{formatCount(c.postCount)} posts</div>
                  </div>
                  <div className={s.contribDot} style={{ background: c.color }} />
                </div>
              ))}
            </div>
          )}

          {contribTab === 'users' && (
            <div className={s.contribList}>
              {tf && topUsers.length === 0 && <div className={s.contribEmpty}>Loading…</div>}
              {!tf && topUsers.length === 0 && <div className={s.contribEmpty}>No data yet</div>}
              {topUsers.map((u, i) => (
                <div key={u.id} className={s.contribRow}>
                  <span className={s.contribRank}>{i < 3 ? rankLabels[i] : <span className={s.contribRankNum}>{i + 1}</span>}</span>
                  <Avatar name={u.name} avatarUrl={u.avatarUrl} size={28} />
                  <div className={s.contribInfo}>
                    <div className={s.contribName}>{u.name}</div>
                    <div className={s.contribSub}>{formatCount(u.postCount)} contributions</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <AdminFeedback />
    </div>
  )
}
